import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import Modal from "src/components/common/Modal";
import Button from "../Button";
import Input from "../Input";
import LoaderFullScreen from "src/components/common/Loader/LoaderFullScreen";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as goalsActions from "src/redux/slices/goalsSlice";
import { IGoal } from "src/types/goals.types";
import { NUMERICS } from "src/constants/regex";
import { validateGoals } from "src/constants/reminders";

interface SetGoalsModalProps {
  show: boolean;
  handleClose: () => void;
  type: string;
}

const SetGoalsModal: React.FC<SetGoalsModalProps> = ({
  show,
  handleClose,
  type,
}) => {
  const [goal, setGoal] = useState<IGoal | null>(null);
  const [goalValue, setGoalValue] = useState(0);
  const [goalError, setGoalError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const { goalsData } = useAppSelector((state) => state.goals);

  const validateGoals = {
    water: { min: 1, max: 5000, unit: "ml" },
    sleep: { min: 1, max: 24, unit: "hours" },
    steps: { min: 1, max: 25000, unit: "steps" },
    exercise: { min: 1, max: 10000, unit: "kcal" },
  } as const;

  useEffect(() => {
    if (patient?.data?.patient_id) {
      getGoals();
    }
  }, [patient]);

  useEffect(() => {
    if (goalsData) {
      const goalData = goalsData?.data?.goals?.find(
        (goal) => goal.goal_type === `${type}_goal`
      );
      setGoal(goalData || null);
      setGoalValue(Math.abs(goalData?.goal_value || 0));
    }
  }, [goalsData]);

  const getGoals = () => {
    new Promise((resolve, reject) => {
      dispatch(
        goalsActions.getGoalsRequest({
          payload: { user_id: patient?.data?.patient_id },
          resolve,
          reject,
        })
      );
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const invalidKeys = ["-", "+", "e", "E", ".", ",", " "];
    if (
      invalidKeys.includes(e.key) ||
      (!/[0-9]/.test(e.key) && e.key.length === 1)
    ) {
      e.preventDefault();
      // setGoalError("Only positive numbers are allowed");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // 1️⃣ Allow empty value for deletion
    if (value === "") {
      setGoalValue(0);
      setGoalError(null);
      return;
    }

    // 2️⃣ Only digits allowed
    if (!/^\d+$/.test(value)) {
      setGoalError("Only positive numbers are allowed");
      return;
    }

    // 3️⃣ Convert to number
    const numericValue = Number(value);
    const { min, max, unit } =
      validateGoals[type as keyof typeof validateGoals];

    // 4️⃣ Check bounds
    const errorMessage =
      numericValue < min || numericValue > max
        ? `Please enter the value between ${min} to ${max} ${unit}`
        : null;
    setGoalError(errorMessage);

    // 5️⃣ Update state
    setGoalValue(numericValue);
  };

  const handleSave = () => {
    if (goalError) {
      return;
    }
    const payload = {
      user_id: patient?.data?.patient_id,
      [`${type}_goal`]: goalValue,
    };
    new Promise((resolve, reject) => {
      dispatch(
        goalsActions.setGoalsRequest({
          payload: JSON.stringify(payload),
          resolve,
          reject,
        })
      );
    })
      .then(() => {
        handleClose();
        toast.success("Goals set successfully");
      })
      .catch((error) => {
        console.error("error", error);
        toast.error("Failed to set goals!");
      });
  };

  if (goalsData?.isLoading) {
    return <LoaderFullScreen show={true} loaderSize="lg" />;
  }

  return (
    <Modal
      show={show}
      showCloseButton
      handleClose={handleClose}
      title={`Set ${type.charAt(0).toUpperCase() + type.slice(1)} Goals`}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-y-3">
          <div className="col-span-1">
            <div className="relative w-full">
              <Input
                label={`Set your daily goal for ${
                  type.charAt(0).toUpperCase() + type.slice(1)
                } Logs`}
                name="goal"
                type="number"
                placeholder={`Enter value`}
                fullWidth
                value={String(goalValue || "")}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                pattern="[0-9]*"
                endAdornment={
                  <span className="text-sm text-gray-500 px-2">
                    {goal?.goal_unit}
                  </span>
                }
                error={!!goalError}
                helperText={goalError || ""}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="relative w-full flex items-center justify-between gap-2">
              <div className="w-full">
                <Button
                  variant="outlined"
                  rounded="full"
                  fullWidth
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </div>
              <div className="w-full">
                <Button
                  variant="contained"
                  rounded="full"
                  fullWidth
                  onClick={handleSave}
                  disabled={goalValue === 0 || !!goalError}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SetGoalsModal;
